import { useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { Camera } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { CameraIcon, DeleteIcon, UserIcon } from '../Icons'
import { showErrorToast } from '../ui/toast'

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string
  selectedFile?: File | null
  onPhotoChange?: (file: File | null) => void
  removed?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
  disabled?: boolean
  onRemove?: () => void
}

export function ProfilePhotoUpload({
  currentPhotoUrl,
  onPhotoChange,
  size = 'lg',
  className,
  disabled = false,
  onRemove,
  selectedFile,
  removed,
}: Readonly<ProfilePhotoUploadProps>) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentPhotoUrl || null
  )
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    if (selectedFile instanceof File) {
      setPreviewUrl(URL.createObjectURL(selectedFile))
    } else if (removed) {
      setPreviewUrl(null)
    } else {
      setPreviewUrl(currentPhotoUrl ?? null)
    }
  }, [currentPhotoUrl, selectedFile, removed])

  const sizeClasses = {
    sm: 'size-16',
    md: 'size-24',
    lg: 'size-32',
  }

  const iconSizes = {
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-6',
  }

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file) {
        const lower = file.name.toLowerCase()
        const isValid = /\.(png|jpg|jpeg)$/.test(lower)
        if (!isValid) {
          showErrorToast('Only .png, .jpg and .jpeg files are allowed')
          return
        }
        setIsUploading(true)

        // Create preview URL
        const preview = URL.createObjectURL(file)
        setPreviewUrl(preview)

        // Simulate upload process
        setTimeout(() => {
          setIsUploading(false)
          onPhotoChange?.(file)
        }, 500)
      }
    },
    [onPhotoChange]
  )

  const onDropRejected = useCallback(() => {
    showErrorToast('Only .png, .jpg and .jpeg files are allowed')
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false,
    disabled: disabled || isUploading,
  })

  const removePhoto = () => {
    setPreviewUrl(null)
    onPhotoChange?.(null)
    onRemove?.()
  }

  return (
    <div className={cn('relative inline-block', className)}>
      <div
        {...getRootProps()}
        className={cn(
          sizeClasses[size],
          'relative rounded-full border-4 border-white shadow-lg bg-ss-neutral-100 overflow-hidden cursor-pointer group transition-all duration-200',
          isDragActive && 'ring-2 ring-ss-blue-500 ring-offset-2',
          disabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <input {...getInputProps()} />

        {/* Photo or placeholder */}
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Profile"
            className="size-full object-cover"
          />
        ) : (
          <div className="size-full flex items-center justify-center bg-ss-blue-50">
            <UserIcon className={cn('size-12 text-ss-neutral-400')} />
          </div>
        )}

        {/* Upload overlay */}
        {!disabled && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <Camera className={cn(iconSizes[size], 'text-white')} />
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
          </div>
        )}
      </div>

      {/* Camera icon overlay - matches Figma design */}
      <div className="absolute bottom-0 right-0 bg-ss-blue-500 rounded-full p-1 border-2 border-white shadow-sm size-9 flex items-center justify-center">
        <Button
          variant="ghost"
          className="bg-transparent hover:bg-transparent focus:ring-0 size-3"
          aria-label={previewUrl ? 'Remove photo' : 'Upload photo'}
          onClick={previewUrl && !disabled ? removePhoto : undefined}
        >
          {previewUrl && !disabled ? (
            <DeleteIcon className="size-4 text-white cursor-pointer" />
          ) : (
            <CameraIcon className="size-4 text-white cursor-pointer" />
          )}
        </Button>
      </div>

      {/* Remove button when photo exists */}
    </div>
  )
}
